import FormField from 'components/FormField';
import {
  AiModelState,
  getPrefixName,
  hideRadioClassName,
  Prefix,
  stores2storeScopes,
} from './share';
import { Radio, Select } from 'antd';
import { useCallback, useMemo } from 'react';
import { updateDropsShipping } from 'views/BootGuide/service';
import { getStoreCount } from 'components/StoreScopes';
import useSmartField from 'hooks/useSmartField';
import ModalMode from './ModalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.DropsShipping;

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      noSupport: t('本店不支持一件代发'),
      support: t('本店支持一件代发'),
    } as const;
  }, [t]);
}

function DropsShipping({ prefix, singleMode }: { prefix?: Prefix; singleMode?: boolean }) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));

  const getHideStyle = (type: FV['value']) =>
    singleMode && value !== type ? { display: 'none' } : undefined;

  return (
    <div className="mt-[6px]">
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2" style={getHideStyle(key)}>
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={value === key}
              onChange={({ target: { checked } }) => checked && onChange(key)}
            >
              {label[key]}
            </Radio>
          </div>
        );
      })}
      <DropShippingType prefix={prefix} singleMode={singleMode} />
    </div>
  );
}

export function DropShippingType({
  prefix,
  singleMode,
}: {
  prefix?: Prefix;
  singleMode?: boolean;
}) {
  const { t } = useTranslate();

  const {
    field: { value },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));

  if (value !== 'support' || (!getStoreCount(storeScopes) && !singleMode)) {
    return <></>;
  }

  return (
    <FormField<FV['dropShippingType']>
      name={getPrefixName('dropShippingType', prefix)}
      validate={(value) => (value ? undefined : t('请选择'))}
    >
      {({ field }) => {
        return (
          <Select
            options={[
              { value: 'SYSTEM_PROVIDES_TRACKING_NUMBER', label: t('系统自动提供快递单号') },
              { value: 'SYSTEM_NOT_PROVIDES_TRACKING_NUMBER', label: t('系统不自动提供快递单号') },
            ]}
            {...field}
          />
        );
      }}
    </FormField>
  );
}

export function DropsShippingBootGuide(props: AiModelState<FV>) {
  const label = useLabel();

  return (
    <ModalMode
      state={props}
      title={props.t('一件代发')}
      tips={props.t('您的店铺是否支持一件代发?以下是一些常见的选项供您选择和调整')}
      form={<DropsShipping />}
      getFirstInitialValues={useCallback(({ stores }): { values: FV[] } => {
        return {
          values: [
            {
              value: 'noSupport',
              storeScopes: stores2storeScopes(stores),
              dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
            },
            {
              value: 'support',
              storeScopes: [],
              dropShippingType: 'SYSTEM_PROVIDES_TRACKING_NUMBER',
            },
          ],
        };
      }, [])}
      updateApi={updateDropsShipping}
      getFirstFormItem={({ value }, index, storeSelect) => {
        return (
          <div>
            <section>
              {label[value]}
              {storeSelect}
            </section>
            <DropShippingType prefix={['values', index]} />
          </div>
        );
      }}
      getFirstFormStoreSelectKey={({ value }) => value}
    />
  );
}

export default DropsShipping;
